import { useSyncExternalStore, type ReactNode } from "react";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import { onSignedInUserChange } from "@/hooks/useAuth";
import { dismissAllToasts } from "@/hooks/use-toast";
import { createQueryClient } from "@/lib/query-client";

/**
 * One QueryClient per signed-in user (#7022).
 *
 * Query keys name an organization or a record, never the viewer, and RLS decides what each user
 * may read, so a result loaded for one user must never answer for the next person on this tab.
 * Clearing a shared client is not enough: a mutation still running for the previous user keeps
 * its callbacks, and a late onSuccess/onError (an optimistic-update rollback, say) would write
 * that user's rows back after the clear. So a change of user replaces the client outright. Every
 * hook reaches the client through useQueryClient(), so the previous user's callbacks can only
 * write into the old client, which nothing reads any more.
 *
 * TanStack binds each mounted query and mutation observer to the client it was created with, so
 * the provider is keyed by generation: the subtree below it remounts on the new client.
 */
type Snapshot = { client: QueryClient; generation: number };

// Each client knows whether its user has gone: see createQueryClient's isRetired.
const newClient = (generation: number) =>
  createQueryClient({ isRetired: () => snapshot.generation !== generation });

let snapshot: Snapshot = { client: newClient(0), generation: 0 };
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;

onSignedInUserChange(() => {
  const previous = snapshot.client;
  const generation = snapshot.generation + 1;
  snapshot = { client: newClient(generation), generation };
  // Toasts live above this provider and outlive it: an Undo or a "view" link raised for the
  // previous user must not be left for the next one to read or act on.
  sonnerToast.dismiss();
  dismissAllToasts();
  // Drop what the previous user loaded. clear() destroys each query, which cancels its request
  // silently (no error telemetry); anything that still lands goes into a client no component reads.
  previous.clear();
  listeners.forEach((listener) => listener());
});

export function UserScopedQueryClientProvider({ children }: { children: ReactNode }) {
  const { client, generation } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <QueryClientProvider key={generation} client={client}>
      {children}
    </QueryClientProvider>
  );
}
